import React, { useEffect, useState, useContext, useRef, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../../components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { 
  Calendar as CalendarIcon, List, ClipboardPaste, ChevronLeft, ChevronRight, ChevronDown, X, Users, Plus, MapPin, MoreVertical, Upload, GanttChart, Briefcase
} from "lucide-react";
import { API, AuthContext } from "../../App";
import { toast } from "sonner";
import {
  isSameDay, format, addDays, subDays, startOfWeek, endOfWeek, subWeeks,
  startOfMonth, endOfMonth, addMonths, subMonths, startOfYear, endOfYear,
  addYears, differenceInCalendarDays,
} from "date-fns";
import { Calendar } from "../../components/ui/calendar";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "../../components/ui/dialog";
// Import extracted job view components
import JobCalendarView from "../../components/jobs/JobCalendarView";
import JobTimelineView from "../../components/jobs/JobTimelineView";
import JobListView from "../../components/jobs/JobListView";
import JobPreviewModal from "../../components/jobs/JobPreviewModal";
import MapJobDetailsModal from "../../components/jobs/MapJobDetailsModal";
import PasteTicketModal from "../../components/jobs/PasteTicketModal";
import JobsMapView from "../../components/jobs/JobsMapView";
import ImportJobsModal from "../../components/jobs/ImportJobsModal";
import CustomerModal from "../../components/customers/CustomerModal";
import useJobStatuses from "../../hooks/useJobStatuses";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator
} from "../../components/ui/dropdown-menu";
import { useTheme } from "../../contexts/ThemeContext";
import { useTimezone } from "../../contexts/TimezoneContext";
import { usePlanLimits } from "../../contexts/PlanLimitContext";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "../../components/ui/select";
import SearchableSelect from "../../components/ui/SearchableSelect";

// Server-side pagination page size — keeps the initial network round-trip
// fast (~25kb for 50 enriched jobs) without making the user paginate too
// aggressively in the common "browse this week" use case.
const PAGE_SIZE = 50;

// ── List-view period filter helpers ─────────────────────────────────
const WEEK_OPTS = { weekStartsOn: 1 };

const buildPresetRange = (preset, today) => {
  switch (preset) {
    case "today": return { start: today, end: today };
    case "yesterday": { const d = subDays(today, 1); return { start: d, end: d }; }
    case "this_week": return { start: startOfWeek(today, WEEK_OPTS), end: endOfWeek(today, WEEK_OPTS) };
    case "last_week": { const w = subWeeks(today, 1); return { start: startOfWeek(w, WEEK_OPTS), end: endOfWeek(w, WEEK_OPTS) }; }
    case "this_month": return { start: startOfMonth(today), end: endOfMonth(today) };
    case "last_month": { const m = subMonths(today, 1); return { start: startOfMonth(m), end: endOfMonth(m) }; }
    case "this_year": return { start: startOfYear(today), end: endOfYear(today) };
    default: return null;
  }
};

const rangeLabel = (range, today) => {
  if (!range) return "All Dates";
  const { start, end } = range;
  if (isSameDay(start, end)) {
    if (isSameDay(start, today)) return "Today";
    if (isSameDay(start, subDays(today, 1))) return "Yesterday";
    return format(start, "EEE, MMM d");
  }
  const presets = [
    ["This Week", buildPresetRange("this_week", today)],
    ["Last Week", buildPresetRange("last_week", today)],
    ["This Month", buildPresetRange("this_month", today)],
    ["Last Month", buildPresetRange("last_month", today)],
    ["This Year", buildPresetRange("this_year", today)],
  ];
  for (const [label, r] of presets) {
    if (isSameDay(start, r.start) && isSameDay(end, r.end)) return label;
  }
  if (isSameDay(start, startOfMonth(start)) && isSameDay(end, endOfMonth(start))) return format(start, "MMMM yyyy");
  if (isSameDay(start, startOfYear(start)) && isSameDay(end, endOfYear(start))) return format(start, "yyyy");
  const sameYear = start.getFullYear() === end.getFullYear();
  return `${format(start, "MMM d")} – ${format(end, sameYear ? "MMM d" : "MMM d, yyyy")}`;
};

const shiftRange = (range, dir, today) => {
  if (!range) {
    const d = dir < 0 ? subDays(today, 1) : addDays(today, 1);
    return { start: d, end: d };
  }
  const { start, end } = range;
  if (isSameDay(start, startOfYear(start)) && isSameDay(end, endOfYear(start))) {
    const y = addYears(start, dir);
    return { start: startOfYear(y), end: endOfYear(y) };
  }
  if (isSameDay(start, startOfMonth(start)) && isSameDay(end, endOfMonth(start))) {
    const m = addMonths(start, dir);
    return { start: startOfMonth(m), end: endOfMonth(m) };
  }
  const span = differenceInCalendarDays(end, start) + 1;
  return { start: addDays(start, dir * span), end: addDays(end, dir * span) };
};

const JOBS_VIEW_STATE_KEY = "jobs_view_state";
const readJobsViewState = () => {
  try {
    return JSON.parse(sessionStorage.getItem(JOBS_VIEW_STATE_KEY)) || {};
  } catch {
    return {};
  }
};
const parseSavedDate = (v) => {
  if (!v) return null;
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
};
// "YYYY-MM-DD" URL param → local Date (avoids UTC shift)
const parseParamDate = (v) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v || "");
  return m ? new Date(+m[1], +m[2] - 1, +m[3]) : null;
};

const JobsPage = () => {
  const { isDark } = useTheme();
  const { user } = useContext(AuthContext);
  const { timezone, formatDate, formatTime, formatDateTime, getCurrentTime, getToday } = useTimezone();
  const { isSmsReadyAsync } = usePlanLimits();
  const isStoreAccount = user?.account_type === "multi_location_store";
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Data states
  const [jobs, setJobs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [jobTypes, setJobTypes] = useState([]);
  const [jobSources, setJobSources] = useState([]);
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [vehicleData, setVehicleData] = useState({ makes: [], models_by_make: {}, years: [] });
  
  // UI states
  const [loading, setLoading] = useState(true);
  const [sendingToTech, setSendingToTech] = useState(null);
  const [sendingReview, setSendingReview] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [jobToDelete, setJobToDelete] = useState(null);
  // Restore the Jobs view (date, month, view mode, list range) after
  // navigating away — e.g. editing a job on /dashboard/jobs/:id/edit
  // unmounts this page; without this, saving/closing dumped users back
  // on today's date. Session-scoped: a fresh browser session starts fresh.
  const savedViewRef = useRef();
  if (savedViewRef.current === undefined) savedViewRef.current = readJobsViewState();
  const savedView = savedViewRef.current;
  // Deep-link support (?view=timeline&date=YYYY-MM-DD) — e.g. dashboard Upcoming Tasks
  const urlView = ["timeline", "calendar", "list", "map"].includes(searchParams.get("view")) ? searchParams.get("view") : null;
  const urlDate = parseParamDate(searchParams.get("date"));
  const [selectedDate, setSelectedDate] = useState(() => urlDate || parseSavedDate(savedView.selectedDate) || getToday());
  const [currentMonth, setCurrentMonth] = useState(() => urlDate || parseSavedDate(savedView.currentMonth) || getToday());
  const [viewMode, setViewMode] = useState(() => urlView || (searchParams.get("attention") ? "list" : savedView.viewMode || "calendar"));
  // Track manual view changes so a late-arriving default-view preference
  // never yanks the user out of a view they explicitly picked.
  const userChangedViewRef = useRef(!!savedView.viewMode || !!searchParams.get("attention") || !!urlView);
  const locViewAppliedRef = useRef(false);
  const changeViewMode = useCallback((v) => {
    userChangedViewRef.current = true;
    setViewMode(v);
  }, []);
  const [previewJob, setPreviewJob] = useState(null);
  const [pasteTicketOpen, setPasteTicketOpen] = useState(false);
  const [importJobsOpen, setImportJobsOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  // "Needs attention" deep-link filter from the dashboard (?attention=unassigned|overdue|deposit)
  const [attentionFilter, setAttentionFilter] = useState(() => searchParams.get("attention") || "");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [techFilter, setTechFilter] = useState("all");
  const [locationFilter, setLocationFilter] = useState("all");
  const [storeLocations, setStoreLocations] = useState([]);
  // Date-range filter for list view: { start: Date, end: Date } | null (= all dates)
  const [listRange, setListRange] = useState(() => {
    if ("listRange" in savedView) {
      if (savedView.listRange === null) return null;
      const s = parseSavedDate(savedView.listRange?.start);
      const e = parseSavedDate(savedView.listRange?.end);
      if (s && e) return { start: s, end: e };
    }
    return { start: getToday(), end: getToday() };
  });
  const [customRangeOpen, setCustomRangeOpen] = useState(false);
  const [draftRange, setDraftRange] = useState(null); // react-day-picker { from, to }
  // Timeline view state — week start (Monday-based) for the Timeline → Week
  // sub-mode. Timeline → Day uses `selectedDate` (shared with Calendar).
  const [weekStart, setWeekStart] = useState(() => parseSavedDate(savedView.weekStart) || startOfWeek(getToday(), { weekStartsOn: 1 }));
  // Map view date range — kept here (not in the map component) so the
  // selection survives opening/closing the job details modal + view switches.
  const [mapRange, setMapRange] = useState(() => ({ start: getToday(), end: getToday() }));
  // Re-sync the auto-initialized "today" dates once the business timezone
  // arrives (first load on a new device — later loads use the cached tz).
  // Only applies when the dates weren't restored from a saved view: near
  // midnight the default-tz "today" can be a DIFFERENT calendar day than
  // the business-tz "today", hiding freshly created jobs on the timeline.
  const datesAutoInitRef = useRef(!savedView.selectedDate && !savedView.weekStart && !savedView.currentMonth);
  const tzAppliedRef = useRef(timezone);
  useEffect(() => {
    if (tzAppliedRef.current === timezone) return;
    tzAppliedRef.current = timezone;
    if (!datesAutoInitRef.current) return;
    const today = getToday();
    setSelectedDate((prev) => (isSameDay(prev, today) ? prev : today));
    setCurrentMonth((prev) => (isSameDay(prev, today) ? prev : today));
    setWeekStart((prev) => {
      const ws = startOfWeek(today, { weekStartsOn: 1 });
      return isSameDay(prev, ws) ? prev : ws;
    });
    setListRange((prev) => {
      if (!prev) return prev; // "all dates" — leave alone
      if (isSameDay(prev.start, prev.end) && !isSameDay(prev.start, today)) {
        return { start: today, end: today };
      }
      return prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timezone]);
  // Persisted Timeline hour preferences from /api/settings/business.
  // null while loading; { start_hour, end_hour } once loaded (may have either
  // field undefined if the user never saved one).
  const [timelineHoursPref, setTimelineHoursPref] = useState(null);
  // Active search query — when non-empty AND >= 2 chars, the date filter is
  // ignored entirely so users can find a job from any time period.
  const [searchQuery, setSearchQuery] = useState("");

  // Persist the current Jobs view so it survives route changes (job edit/create)
  useEffect(() => {
    try {
      sessionStorage.setItem(JOBS_VIEW_STATE_KEY, JSON.stringify({
        selectedDate: selectedDate?.toISOString?.() || null,
        currentMonth: currentMonth?.toISOString?.() || null,
        viewMode,
        listRange: listRange
          ? { start: listRange.start?.toISOString?.(), end: listRange.end?.toISOString?.() }
          : null,
        weekStart: weekStart?.toISOString?.() || null,
      }));
    } catch { /* storage unavailable — non-fatal */ }
  }, [selectedDate, currentMonth, viewMode, listRange, weekStart]);

  // Server-side pagination + stats. The Jobs page now fetches a bounded
  // window from the backend (current month / one date / 50-row paged list)
  // instead of slurping every job and filtering client-side. Status pill
  // counts come from /api/jobs/stats so they stay accurate across pages.
  //
  // Pagination strategy — KEYSET CURSOR (not offset). Stays O(log n) at
  // 100k+ jobs because the backend resumes from the last row's
  // (scheduled_date, job_id) instead of `.skip(N)`-ing through everything
  // we already saw. We hold the next-page cursor returned by the previous
  // request in `nextCursorRef` and pass it back as `?cursor=…`.
  const [stats, setStats] = useState({ total: 0, counts_by_status: {} });
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const fetchSeqRef = useRef(0);          // monotonic counter for cancellation
  const debounceRef = useRef(null);       // search debounce timer
  const nextCursorRef = useRef("");       // backend's X-Next-Cursor for next page

  // ─────────────────────────────────────────────────────────────────────
  // Build the query string for /api/jobs and /api/jobs/stats from the
  // current view + filter state. Single source of truth so the two
  // endpoints stay in lockstep.
  //
  // Behaviour matrix (kept identical to the previous client-side pipeline):
  //   • search active (q.length >= 2)  → ignore date filter, search all time
  //   • viewMode === "calendar"        → fetch the visible month
  //   • viewMode === "list" + listRange → fetch that date range
  //   • viewMode === "list" + no date  → paginated all-time newest-first
  // The status filter is applied server-side EXCEPT when computing stats
  // (the stats endpoint breaks down BY status so it's omitted from those
  // params).
  // ─────────────────────────────────────────────────────────────────────
  const searchActive = searchQuery.trim().length >= 2;

  const buildJobsQuery = useCallback(({ cursor = "", statsOnly = false } = {}) => {
    const p = new URLSearchParams();
    if (!statsOnly) {
      p.set("limit", String(PAGE_SIZE));
      // Keyset cursor for the next page — server returns it in X-Next-Cursor.
      if (cursor) p.set("cursor", cursor);
    }
    if (searchActive) {
      p.set("q", searchQuery.trim());
      // Search across all time — give a wider window for the result set.
      if (!statsOnly) p.set("limit", "200");
    } else if (viewMode === "calendar" || viewMode === "timeline") {
      // Both calendar and timeline fetch a full month window — timeline
      // navigates within that month freely (day or week sub-modes) without
      // requiring an additional API call.
      const refDate = viewMode === "calendar" ? currentMonth : selectedDate;
      p.set("month", format(refDate, "yyyy-MM"));
      // A single month is bounded; lift the page size to render dots correctly.
      if (!statsOnly) p.set("limit", "500");
    } else if (viewMode === "map") {
      p.set("start_date", format(mapRange.start, "yyyy-MM-dd"));
      p.set("end_date", `${format(mapRange.end, "yyyy-MM-dd")}T23:59:59`);
      if (!statsOnly) p.set("limit", "300");
    } else if (attentionFilter) {
      // Attention deep-links scan all dates; the server filters below bound the set.
    } else if (listRange) {
      p.set("start_date", format(listRange.start, "yyyy-MM-dd"));
      p.set("end_date", `${format(listRange.end, "yyyy-MM-dd")}T23:59:59`);
    }
    if (!statsOnly && statusFilter !== "all") p.set("status", statusFilter);
    if (techFilter !== "all") p.set("assigned_to", techFilter);
    if (sourceFilter !== "all") p.set("source_id", sourceFilter);
    if (!statsOnly && attentionFilter === "unassigned") {
      p.set("assigned_to", "unassigned");
    } else if (!statsOnly && attentionFilter === "overdue") {
      p.set("status", "scheduled");
      p.set("end_date", `${format(new Date(getToday().getTime() - 86400000), "yyyy-MM-dd")}T23:59:59`);
    } else if (!statsOnly && attentionFilter === "deposit") {
      p.set("status", "deposit_collected");
    }
    return p;
  }, [searchActive, searchQuery, viewMode, currentMonth, selectedDate, listRange, mapRange, statusFilter, techFilter, sourceFilter, attentionFilter, getToday]);

  // True when the current view is cursor-paginated (list view, no search).
  // Calendar/search modes are bounded so they don't need infinite scroll.
  const isPaginatedMode = viewMode === "list" && !searchActive;

  // Parse a scheduled_time string ("2:00 PM", "14:00", "09:38") to minutes since midnight
  function parseTimeStr(s) {
    if (!s) return null;
    const ampm = s.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (ampm) {
      let h = parseInt(ampm[1], 10);
      const m = parseInt(ampm[2], 10);
      const p = ampm[3].toUpperCase();
      if (p === "PM" && h < 12) h += 12;
      if (p === "AM" && h === 12) h = 0;
      return h * 60 + m;
    }
    const h24 = s.match(/^(\d{1,2}):(\d{2})$/);
    if (h24) return parseInt(h24[1], 10) * 60 + parseInt(h24[2], 10);
    return null;
  }

  // Build a comparable timestamp from scheduled_date + scheduled_time
  function getScheduledMs(job) {
    if (!job.scheduled_date) return null;
    const d = new Date(job.scheduled_date);
    if (isNaN(d.getTime())) return null;
    // If scheduled_time field has an explicit time, override the date's time component
    const st = (job.scheduled_time || "").trim();
    if (st) {
      const mins = parseTimeStr(st);
      if (mins !== null) {
        // Create a new date with the same day but the explicit time
        const base = new Date(d);
        base.setHours(Math.floor(mins / 60), mins % 60, 0, 0);
        return base.getTime();
      }
    }
    // Otherwise use the full datetime as-is (time is embedded in scheduled_date)
    return d.getTime();
  }

  // Server-side fetch produces jobs already sorted by scheduled_date desc
  // and pre-filtered for status / tech / date / search. We keep one tiny
  // fallback re-sort so manually-injected items (e.g. a fresh create_job
  // response) land in the right place.
  const sortedJobs = [...jobs].sort((a, b) => {
    const aMs = getScheduledMs(a);
    const bMs = getScheduledMs(b);
    if (aMs === null && bMs === null) return new Date(b.created_at || 0) - new Date(a.created_at || 0);
    if (aMs === null) return 1;
    if (bMs === null) return -1;
    if (aMs !== bMs) return bMs - aMs;
    const ca = new Date(b.created_at || 0) - new Date(a.created_at || 0);
    if (ca !== 0) return ca;
    return (b.job_id || "").localeCompare(a.job_id || "");
  });

  // Location filter — the only remaining client-side filter (multi-location
  // store accounts only). We keep this client-side because the dataset is
  // already bounded by month/date/status on the server, AND the field is
  // null for non-store accounts so server-side filtering would be a no-op.
  const baseJobs = attentionFilter === "unassigned"
    ? sortedJobs.filter(j => !["completed", "cancelled"].includes(j.status))
    : sortedJobs;
  const filteredJobs = locationFilter === "all"
    ? baseJobs
    : baseJobs.filter(j => j.store_location_id === locationFilter);

  // Status pill counts come from /api/jobs/stats so they stay accurate even
  // while the list view is paginated. The "all" pill mirrors the total.
  const statusCounts = stats.counts_by_status || {};
  const { statuses: companyStatuses } = useJobStatuses();

  // Handle URL action parameter (for mobile quick action)
  useEffect(() => {
    const action = searchParams.get("action");
    if (action === "new") {
      setSearchParams({});
      navigate("/dashboard/jobs/new");
    }
  }, [searchParams, setSearchParams, navigate]);

  // Strip consumed deep-link params (?view/?date) so refresh/back stays clean
  useEffect(() => {
    if (searchParams.get("view") || searchParams.get("date")) {
      const next = new URLSearchParams(searchParams);
      next.delete("view");
      next.delete("date");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle URL job_id parameter to open job details
  useEffect(() => {
    const jobId = searchParams.get("job");
    if (!jobId) return;
    setSearchParams({});
    navigate(`/dashboard/jobs/${jobId}/edit`);
  }, [searchParams, setSearchParams, navigate]);

  // ─────────────────────────────────────────────────────────────────────
  // Fetch the heavy `jobs` list with the server-side filters built above.
  // Append=true is used by the infinite-scroll handler in list/all-dates
  // mode; otherwise we replace the dataset.
  // The next-page cursor returned by the backend (X-Next-Cursor header) is
  // stashed in `nextCursorRef` so handleLoadMore can resume from it.
  // ─────────────────────────────────────────────────────────────────────
  const fetchJobs = useCallback(async ({ cursor = "", append = false } = {}) => {
    const token = localStorage.getItem("token");
    if (!token) { setLoading(false); return; }
    const seq = ++fetchSeqRef.current;
    if (!append) setLoading(true);
    else setLoadingMore(true);
    try {
      const params = buildJobsQuery({ cursor });
      const res = await axios.get(`${API}/jobs?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      if (seq !== fetchSeqRef.current) return; // stale request — discard
      const data = Array.isArray(res.data) ? res.data : [];
      setJobs(prev => append ? [...prev, ...data] : data);
      // Capture the keyset cursor for the next page; empty string ⇒ end of list.
      nextCursorRef.current = res.headers?.["x-next-cursor"] || "";
      const isPaging = !searchActive && viewMode === "list";
      setHasMore(isPaging && Boolean(nextCursorRef.current));
    } catch (e) {
      if (!append) toast.error("Failed to load jobs");
    } finally {
      if (seq === fetchSeqRef.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, [buildJobsQuery, searchActive, viewMode]);

  const fetchStats = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) return;
    try {
      const params = buildJobsQuery({ statsOnly: true });
      const res = await axios.get(`${API}/jobs/stats?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      setStats(res.data || { total: 0, counts_by_status: {} });
    } catch {
      // Stats are non-critical — pills will show 0 if this fails.
    }
  }, [buildJobsQuery]);

  // Fetch the supporting reference data (customers, technicians, sources,
  // job types, payment types, vehicle data, store locations) ONCE on mount.
  // These are small + cached and feed the job dialog dropdowns.
  const fetchReferenceData = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("Please log in to view jobs");
      setLoading(false);
      return;
    }
    const config = { headers: { Authorization: `Bearer ${token}` } };
    try {
      const [customersRes, techniciansRes, jobTypesRes, sourcesRes, paymentsRes, vehiclesRes] = await Promise.all([
        axios.get(`${API}/customers`, config).catch(() => ({ data: [] })),
        axios.get(`${API}/technicians`, config).catch(() => ({ data: [] })),
        axios.get(`${API}/settings/job-types`, config).catch(() => ({ data: [] })),
        axios.get(`${API}/settings/job-sources`, config).catch(() => ({ data: [] })),
        axios.get(`${API}/settings/payment-types`, config).catch(() => ({ data: [] })),
        axios.get(`${API}/settings/vehicles/all`, config).catch(() => ({ data: { makes: [], models_by_make: {}, years: [] } })),
      ]);
      const techniciansData = Array.isArray(techniciansRes.data) ? techniciansRes.data : [];
      const technicians = techniciansData.map(t => ({ ...t, member_id: t.technician_id, status: t.status || "active" }));
      setCustomers(Array.isArray(customersRes.data) ? customersRes.data : []);
      setTeamMembers(technicians);
      setJobTypes(Array.isArray(jobTypesRes.data) ? jobTypesRes.data : []);
      setJobSources(Array.isArray(sourcesRes.data) ? sourcesRes.data : []);
      setPaymentTypes(Array.isArray(paymentsRes.data) ? paymentsRes.data : []);
      setVehicleData(vehiclesRes.data || { makes: [], models_by_make: {}, years: [] });
      if (isStoreAccount) {
        try {
          const locRes = await axios.get(`${API}/store/locations`, config);
          setStoreLocations((locRes.data.locations || []).filter(l => l.active));
        } catch {}
      }
    } catch {
      toast.error("Failed to load reference data");
    }
  }, [isStoreAccount]);

  // Re-fetch jobs + stats whenever the dimensions that affect the query change.
  // Search input is debounced 300ms so we don't hammer the API on every keystroke.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const delay = searchActive ? 300 : 0;
    debounceRef.current = setTimeout(() => {
      // Filter change ⇒ start a fresh keyset walk.
      nextCursorRef.current = "";
      fetchJobs({ cursor: "", append: false });
      fetchStats();
    }, delay);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [fetchJobs, fetchStats, searchActive]);

  // Reference data loads once on mount.
  useEffect(() => {
    fetchReferenceData();
  }, [fetchReferenceData]);

  // Load Timeline hour preferences from /api/settings/business once on mount.
  // Failures fall back to component defaults (7 AM – 7 PM) and don't toast —
  // the timeline is fully functional without persistence.
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;
    axios.get(`${API}/settings/business`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        const s = r.data || {};
        setTimelineHoursPref({
          start_hour: typeof s.timeline_start_hour === "number" ? s.timeline_start_hour : undefined,
          end_hour: typeof s.timeline_end_hour === "number" ? s.timeline_end_hour : undefined,
        });
        // Apply the account-wide default Jobs view (Settings → Business Info)
        // unless the user already switched views or a location override won.
        const dv = s.default_jobs_view;
        if (["calendar", "timeline", "list", "map"].includes(dv) && !userChangedViewRef.current && !locViewAppliedRef.current) {
          setViewMode(dv);
        }
      })
      .catch(() => setTimelineHoursPref({}));
  }, []);

  // Per-location Default Jobs View override — the default store location's
  // setting wins over the account-wide one (store accounts only).
  useEffect(() => {
    if (locViewAppliedRef.current || userChangedViewRef.current) return;
    if (!isStoreAccount || storeLocations.length === 0) return;
    const def = storeLocations.find(l => l.is_default) || storeLocations[0];
    const dv = def?.default_jobs_view;
    if (["calendar", "timeline", "list", "map"].includes(dv)) {
      locViewAppliedRef.current = true;
      setViewMode(dv);
    }
  }, [isStoreAccount, storeLocations]);

  // Save updated Timeline hour preferences back to /api/settings/business.
  // PATCH semantics — only sends the two fields so other settings stay intact.
  const saveTimelineHoursPref = useCallback(async (start_hour, end_hour) => {
    const token = localStorage.getItem("token");
    if (!token) return;
    try {
      await axios.put(`${API}/settings/business`, {
        timeline_start_hour: start_hour,
        timeline_end_hour: end_hour,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setTimelineHoursPref({ start_hour, end_hour });
      toast.success("Timeline hours saved");
    } catch (e) {
      toast.error("Failed to save timeline hours");
    }
  }, []);

  // Refresh the paged list to page 0 + refresh stats. Used by the form
  // save / delete success callbacks.
  const fetchData = useCallback(async () => {
    nextCursorRef.current = "";
    await Promise.all([
      fetchJobs({ cursor: "", append: false }),
      fetchStats(),
    ]);
  }, [fetchJobs, fetchStats]);

  // Infinite-scroll callback used by JobListView when isPaginatedMode is true.
  // Resumes from the last page's keyset cursor → O(log n) regardless of depth.
  const handleLoadMore = useCallback(() => {
    if (loadingMore || !hasMore || !nextCursorRef.current) return;
    fetchJobs({ cursor: nextCursorRef.current, append: true });
  }, [loadingMore, hasMore, fetchJobs]);

  // Handle edit job — full-page form (crash-safe, no modal focus trap)
  const handleEdit = (job) => {
    navigate(`/dashboard/jobs/${job.job_id}/edit`);
  };

  // Quick-assign (map details modal) saved — reflect immediately in the open
  // modal + the loaded job list without a full refetch.
  const applyAssignmentLocal = useCallback((jobId, patch) => {
    setJobs((prev) => prev.map((j) => (j.job_id === jobId ? { ...j, ...patch } : j)));
    setPreviewJob((prev) => (prev && prev.job_id === jobId ? { ...prev, ...patch } : prev));
  }, []);

  // Bulk assignment from the map — optimistic local update + background refresh
  const handleBulkAssigned = useCallback((jobIds, patch) => {
    if (jobIds?.length) {
      setJobs((prev) => prev.map((j) => (jobIds.includes(j.job_id) ? { ...j, ...patch } : j)));
    }
    fetchData();
  }, [fetchData]);

  // Technician color changed from the map legend — keep the shared list in sync
  const handleTechColorChange = useCallback((techId, color) => {
    setTeamMembers((prev) => prev.map((t) => (t.technician_id === techId ? { ...t, color } : t)));
  }, []);

  // Quick "New Job for Customer" from the job preview modal
  const handleNewJobForCustomer = (job) => {
    navigate("/dashboard/jobs/new", {
      state: {
        customerPrefill: {
          customer_id: job.customer_id || "",
          customer_name: job.customer_name || "",
          customer_phone: job.customer_phone || "",
          customer_email: job.customer_email || "",
          customer_address: job.customer_address || "",
          address_city: job.address_city || "",
          address_state: job.address_state || "",
          zip_code: job.zip_code || "",
        },
      },
    });
  };

  // Handle delete confirmation
  const confirmDelete = (job) => {
    setJobToDelete(job);
    setDeleteDialogOpen(true);
  };

  // ── Cross-navigation: Job preview ↔ Customer profile ──────────────
  const [jobCustomer, setJobCustomer] = useState(null);
  const [jobCustomerOpen, setJobCustomerOpen] = useState(false);

  const openCustomerFromJob = async (job) => {
    if (!job?.customer_id) return;
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/customers/${job.customer_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPreviewJob(null);
      setJobCustomer(res.data);
      setJobCustomerOpen(true);
    } catch {
      toast.error("Failed to load customer profile");
    }
  };

  const openJobFromCustomer = async (job) => {
    if (!job?.job_id) return;
    setJobCustomerOpen(false);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/jobs/${job.job_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPreviewJob(res.data);
    } catch {
      setPreviewJob(job);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!jobToDelete) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/jobs/${jobToDelete.job_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Job deleted successfully");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete job");
    } finally {
      setDeleteDialogOpen(false);
      setJobToDelete(null);
    }
  };

  // Handle send reminder SMS
  const handleSendReminder = async (job) => {
    if (!(await isSmsReadyAsync())) return;
    try {
      const token = localStorage.getItem("token");
      const customerEmail = customers.find(c => c.customer_id === job.customer_id)?.email;
      await axios.post(`${API}/notifications/send-reminder`, {
        job_id: job.job_id,
        customer_phone: job.customer_phone,
        customer_email: customerEmail,
        customer_name: job.customer_name,
        job_title: job.title,
        scheduled_date: job.scheduled_date,
        scheduled_time: job.scheduled_time,
        address: job.customer_address
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Reminder sent successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send reminder");
    }
  };

  // Handle send to tech
  const handleSendToTech = async (job) => {
    if (!job.assigned_to) {
      toast.error("No technician assigned to this job");
      return;
    }
    if (!(await isSmsReadyAsync())) return;
    setSendingToTech(job.job_id);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(`${API}/technicians/jobs/${job.job_id}/send-assignment`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data?.masking_warning) toast.warning(res.data.masking_warning, { duration: 9000 });
      toast.success("Job details sent to technician");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send to technician");
    } finally {
      setSendingToTech(null);
    }
  };

  // Handle send review request
  const handleSendReviewRequest = async (job) => {
    if (!(await isSmsReadyAsync())) return;
    setSendingReview(job.job_id);
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/jobs/${job.job_id}/send-review-request`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Review request sent to customer");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send review request");
    } finally {
      setSendingReview(null);
    }
  };

  // Open new job page
  const openNewJobDialog = () => {
    navigate("/dashboard/jobs/new");
  };

  // Handle parsed ticket data — open job page pre-filled
  const handleTicketParsed = (data) => {
    navigate("/dashboard/jobs/new", { state: { ticketPrefill: data } });
  };

  const getJobsForDate = (date) => {
    // The loaded `filteredJobs` is already the right scope — month-bounded
    // in calendar mode, day-bounded in list mode, search-result-set when
    // searching. Just bucket by day.
    return filteredJobs.filter(job => job.scheduled_date && isSameDay(new Date(job.scheduled_date), date));
  };

  // Quick rename from the job card (title only)
  const handleRenameJob = async (job, title) => {
    try {
      const token = localStorage.getItem("token");
      await axios.patch(`${API}/jobs/${job.job_id}/title`, { title }, { headers: { Authorization: `Bearer ${token}` } });
      setJobs(prev => prev.map(j => (j.job_id === job.job_id ? { ...j, title } : j)));
      setPreviewJob(prev => (prev && prev.job_id === job.job_id ? { ...prev, title } : prev));
      toast.success("Job renamed");
    } catch {
      toast.error("Failed to rename job");
    }
  };

  // source_id → name map for the card's job-source chip
  const jobSourceMap = React.useMemo(
    () => Object.fromEntries((jobSources || []).map(s => [s.source_id, s.name])),
    [jobSources]
  );

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="jobs-page">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-foreground font-['Manrope']">Jobs & Schedule</h2>
            <p className="text-sm text-muted-foreground">Manage and schedule your service jobs</p>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
            {/* Technician Filter — searchable to handle large teams */}
            <SearchableSelect
              value={techFilter}
              onChange={setTechFilter}
              options={[
                { value: "all", label: "All Technicians" },
                { value: "unassigned", label: "Unassigned" },
                ...teamMembers
                  .filter(t => t.status === "active")
                  .map(t => ({ value: t.member_id, label: t.name })),
              ]}
              placeholder="All Technicians"
              searchThreshold={4}
              className="w-[160px] sm:w-[180px]"
              triggerClassName="h-8 border-theme bg-theme-surface text-sm text-foreground"
              triggerIcon={<Users className="w-3.5 h-3.5 shrink-0 text-muted-foreground" />}
              data-testid="tech-filter-select"
            />

            {/* Location Filter (store accounts only) — searchable for chains */}
            {isStoreAccount && storeLocations.length > 0 && (
              <SearchableSelect
                value={locationFilter}
                onChange={setLocationFilter}
                options={[
                  { value: "all", label: "All Locations" },
                  ...storeLocations.map(loc => ({
                    value: loc.location_id,
                    label: `${loc.name}${loc.code ? ` (${loc.code})` : ""}`,
                  })),
                ]}
                placeholder="All Locations"
                searchThreshold={4}
                className="w-[160px] sm:w-[180px]"
                triggerClassName="h-8 border-theme bg-theme-surface text-sm text-foreground"
                triggerIcon={<MapPin className="w-3.5 h-3.5 shrink-0 text-blue-500" />}
                data-testid="location-filter-select"
              />
            )}

            {/* View Mode Toggle */}
            <div className="flex bg-theme-surface rounded-lg p-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => changeViewMode("calendar")}
                className={`px-3 ${viewMode === "calendar" ? "bg-[#FFCC00] text-black hover:bg-[#FFCC00]" : "text-muted-foreground hover:text-foreground hover:bg-theme-hover"}`}
                data-testid="view-mode-calendar"
              >
                <CalendarIcon className="w-4 h-4 sm:mr-1.5" />
                <span className="hidden sm:inline">Calendar</span>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => changeViewMode("timeline")}
                className={`px-3 ${viewMode === "timeline" ? "bg-[#FFCC00] text-black hover:bg-[#FFCC00]" : "text-muted-foreground hover:text-foreground hover:bg-theme-hover"}`}
                data-testid="view-mode-timeline"
              >
                <GanttChart className="w-4 h-4 sm:mr-1.5" />
                <span className="hidden sm:inline">Timeline</span>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => changeViewMode("list")}
                className={`px-3 ${viewMode === "list" ? "bg-[#FFCC00] text-black hover:bg-[#FFCC00]" : "text-muted-foreground hover:text-foreground hover:bg-theme-hover"}`}
                data-testid="view-mode-list"
              >
                <List className="w-4 h-4 sm:mr-1.5" />
                <span className="hidden sm:inline">List</span>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => changeViewMode("map")}
                className={`px-3 ${viewMode === "map" ? "bg-[#FFCC00] text-black hover:bg-[#FFCC00]" : "text-muted-foreground hover:text-foreground hover:bg-theme-hover"}`}
                data-testid="view-mode-map"
              >
                <MapPin className="w-4 h-4 sm:mr-1.5" />
                <span className="hidden sm:inline">Map</span>
              </Button>
            </div>

            {/* Paste Ticket Button */}
            <Button
              variant="outline"
              size="sm"
              className="border-border text-foreground hover:bg-theme-hover whitespace-nowrap px-2.5 sm:px-3"
              onClick={() => setPasteTicketOpen(true)}
              data-testid="paste-ticket-btn"
            >
              <ClipboardPaste className="w-4 h-4 sm:mr-1.5 shrink-0" />
              <span className="hidden sm:inline">Paste Ticket</span>
            </Button>

            {/* Paste Ticket Modal */}
            <PasteTicketModal
              open={pasteTicketOpen}
              onOpenChange={setPasteTicketOpen}
              onTicketParsed={handleTicketParsed}
            />

            {/* Import Jobs Modal */}
            <ImportJobsModal
              open={importJobsOpen}
              onOpenChange={setImportJobsOpen}
              onImported={() => fetchData()}
            />

            {/* New Job — opens the full-page form (crash-safe) */}
            <Button className="btn-primary whitespace-nowrap px-2.5 sm:px-3" size="sm" onClick={openNewJobDialog} data-testid="add-job-btn">
              <Plus className="w-4 h-4 sm:mr-1.5 shrink-0" />
              <span className="hidden sm:inline">New Job</span>
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="border-border text-foreground hover:bg-theme-hover px-2"
                  data-testid="jobs-more-menu-btn"
                  aria-label="More actions"
                >
                  <MoreVertical className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setImportJobsOpen(true)} data-testid="import-jobs-menu-item">
                  <Upload className="w-4 h-4 mr-2" /> Import Jobs (Uleadz)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* List/Map Filters: Date Navigator + Status */}
      {(viewMode === "list" || viewMode === "map") && (
        <div className="flex flex-col gap-2 -mt-2" data-testid="list-filters">
          {/* Search-active banner */}
          {searchActive && (
            <div
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#FFCC00]/10 border border-[#FFCC00]/30 text-[11px] text-foreground"
              data-testid="search-active-banner"
            >
              <span className="font-medium">Searching:</span>
              <span className="font-mono">&ldquo;{searchQuery.trim()}&rdquo;</span>
              <span className="text-muted-foreground ml-1">— date filter ignored, {stats.total || 0} match{(stats.total || 0) === 1 ? "" : "es"}</span>
            </div>
          )}
          {/* Date Navigator — list view only (the map has its own) — dimmed while search is active */}
          {viewMode === "list" && (
          <div
            className={`flex items-center gap-2 transition-opacity ${searchActive ? "opacity-40 pointer-events-none" : ""}`}
            data-testid="date-filter"
            aria-disabled={searchActive}
          >
            <div className="flex items-center bg-theme-surface rounded-lg p-0.5">
              <Button variant="ghost" size="icon" onClick={() => setListRange(r => shiftRange(r, -1, getToday()))}
                className="h-7 w-7 text-muted-foreground hover:text-foreground hover:bg-theme-hover rounded-md" data-testid="date-prev">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-foreground hover:bg-theme-hover rounded-md transition-colors whitespace-nowrap"
                    data-testid="date-period-btn"
                  >
                    {rangeLabel(listRange, getToday())}
                    <ChevronDown className="w-3 h-3 text-muted-foreground" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-44">
                  {[
                    { key: "today", label: "Today" },
                    { key: "yesterday", label: "Yesterday" },
                    { key: "this_week", label: "This Week" },
                    { key: "last_week", label: "Last Week" },
                    { key: "this_month", label: "This Month" },
                    { key: "last_month", label: "Last Month" },
                    { key: "this_year", label: "This Year" },
                  ].map((opt) => (
                    <DropdownMenuItem
                      key={opt.key}
                      onClick={() => setListRange(buildPresetRange(opt.key, getToday()))}
                      data-testid={`date-period-${opt.key}`}
                    >
                      {opt.label}
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => {
                      setDraftRange(listRange ? { from: listRange.start, to: listRange.end } : null);
                      setCustomRangeOpen(true);
                    }}
                    data-testid="date-period-custom"
                  >
                    <CalendarIcon className="w-3.5 h-3.5 mr-2" /> Custom Range…
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => { setListRange(null); }}
                    data-testid="date-period-all"
                  >
                    All Dates
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button variant="ghost" size="icon" onClick={() => setListRange(r => shiftRange(r, 1, getToday()))}
                className="h-7 w-7 text-muted-foreground hover:text-foreground hover:bg-theme-hover rounded-md" data-testid="date-next">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
            {listRange && (
              <button
                onClick={() => { setListRange(null); setStatusFilter("all"); }}
                className="flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-medium bg-theme-surface text-muted-foreground hover:text-foreground hover:bg-theme-hover transition-colors"
                data-testid="date-clear"
              >
                All Dates <X className="w-3 h-3" />
              </button>
            )}
            {listRange && !(isSameDay(listRange.start, getToday()) && isSameDay(listRange.end, getToday())) && (
              <span className="text-xs text-muted-foreground hidden sm:inline">
                {isSameDay(listRange.start, listRange.end)
                  ? format(listRange.start, "EEEE, MMMM d, yyyy")
                  : `${format(listRange.start, "MMM d, yyyy")} – ${format(listRange.end, "MMM d, yyyy")}`}
              </span>
            )}
          </div>
          )}
          {/* Active job-source filter (set by clicking a job's source chip) */}
          {sourceFilter !== "all" && (
            <div className="flex items-center gap-2 pb-1">
              <button
                onClick={() => setSourceFilter("all")}
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium bg-purple-500/20 text-purple-400 ring-1 ring-purple-500/40 hover:bg-purple-500/30 transition-colors"
                title="Clear source filter"
                data-testid="active-source-filter"
              >
                <Briefcase className="w-3 h-3" />
                Source: {jobSourceMap[sourceFilter] || "Unknown"}
                <X className="w-3 h-3" />
              </button>
            </div>
          )}
          {attentionFilter && (
            <div className="flex items-center gap-2 mb-2" data-testid="attention-filter-banner">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold bg-amber-500/15 text-amber-600 dark:text-amber-400 ring-1 ring-amber-500/40">
                Needs attention: {attentionFilter === "unassigned" ? "Unassigned jobs" : attentionFilter === "overdue" ? "Overdue jobs (past scheduled date)" : "Deposit collected — still open"}
                <button onClick={() => { setAttentionFilter(""); setSearchParams({}, { replace: true }); }} className="hover:text-foreground" data-testid="attention-filter-clear">
                  <X className="w-3 h-3" />
                </button>
              </span>
            </div>
          )}
          {/* Status Pills — driven by the company's configured statuses */}
          <div className="flex items-center gap-2 overflow-x-auto pb-1" data-testid="status-filters">
            {(companyStatuses.length ? [
              { key: "all", label: "All", count: stats.total || 0 },
              ...companyStatuses
                .filter((s) => !s.archived || (statusCounts[s.id] || 0) > 0)
                .map((s) => ({ key: s.id, label: s.label, count: statusCounts[s.id] || 0, hex: s.color })),
            ] : [
              { key: "all", label: "All", count: stats.total || 0 },
              { key: "estimate", label: "Estimate", count: statusCounts.estimate || 0, hex: "#f97316" },
              { key: "pending", label: "Pending", count: statusCounts.pending || 0, hex: "#8b5cf6" },
              { key: "scheduled", label: "Scheduled", count: statusCounts.scheduled || 0, hex: "#3b82f6" },
              { key: "in_progress", label: "In Progress", count: statusCounts.in_progress || 0, hex: "#f59e0b" },
              { key: "deposit_collected", label: "Deposit Collected", count: statusCounts.deposit_collected || 0, hex: "#14b8a6" },
              { key: "completed", label: "Completed", count: statusCounts.completed || 0, hex: "#22c55e" },
              { key: "completed_pending_payment", label: "Pending Payment", count: statusCounts.completed_pending_payment || 0, hex: "#d97706" },
              { key: "cancelled", label: "Cancelled", count: statusCounts.cancelled || 0, hex: "#ef4444" },
            ]).map((f) => {
              const isActive = statusFilter === f.key;
              return (
              <button
                key={f.key}
                onClick={() => setStatusFilter(f.key)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
                  isActive && f.key === "all"
                    ? "bg-foreground text-background"
                    : isActive
                      ? ""
                      : "bg-theme-surface text-muted-foreground hover:text-foreground hover:bg-theme-hover"
                }`}
                style={isActive && f.key !== "all" && f.hex ? { backgroundColor: `${f.hex}2e`, color: f.hex, boxShadow: `inset 0 0 0 1px ${f.hex}66` } : undefined}
                data-testid={`filter-${f.key}`}
              >
                {f.label}
                <span className={`text-[10px] rounded-full px-1.5 py-0.5 leading-none ${
                  statusFilter === f.key ? "bg-white/20" : "bg-theme-hover"
                }`}>{f.count}</span>
              </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Custom date range picker */}
      <Dialog open={customRangeOpen} onOpenChange={setCustomRangeOpen}>
        <DialogContent className="bg-background border-border w-auto max-w-fit" data-testid="custom-range-dialog">
          <DialogHeader>
            <DialogTitle className="text-foreground text-base">Custom Date Range</DialogTitle>
          </DialogHeader>
          <Calendar
            mode="range"
            numberOfMonths={2}
            selected={draftRange}
            onSelect={setDraftRange}
            defaultMonth={draftRange?.from || getToday()}
            data-testid="custom-range-calendar"
          />
          <DialogFooter className="gap-2">
            <Button variant="outline" size="sm" onClick={() => setCustomRangeOpen(false)}
              className="border-border text-foreground" data-testid="custom-range-cancel">
              Cancel
            </Button>
            <Button size="sm" disabled={!draftRange?.from} className="btn-primary"
              onClick={() => {
                if (!draftRange?.from) return;
                setListRange({ start: draftRange.from, end: draftRange.to || draftRange.from });
                setCustomRangeOpen(false);
              }}
              data-testid="custom-range-apply"
            >
              Apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-background border-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-foreground">Delete Job</AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground">
              Are you sure you want to delete &quot;{jobToDelete?.title}&quot;? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-theme-surface text-foreground border-theme hover:bg-theme-hover">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Main Content */}
      {loading && viewMode !== "map" ? (
        // Skeleton loader — gives users an immediate sense of progress while
        // the first page of jobs loads. Far less jarring than a centered
        // spinner on slow connections.
        <div className="space-y-3" data-testid="jobs-skeleton">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="rounded-xl bg-card/60 border border-border/60 p-4 animate-pulse"
              style={{ animationDelay: `${i * 80}ms` }}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="h-3 w-24 rounded bg-theme-hover" />
                <div className="h-5 w-16 rounded bg-theme-hover" />
              </div>
              <div className="h-4 w-3/4 rounded bg-theme-hover mb-2" />
              <div className="h-3 w-1/2 rounded bg-theme-hover" />
            </div>
          ))}
        </div>
      ) : viewMode === "map" ? (
        <JobsMapView
          jobs={filteredJobs}
          range={mapRange}
          setRange={setMapRange}
          onPreview={setPreviewJob}
          getToday={getToday}
          loading={loading}
          teamMembers={teamMembers}
          onBulkAssigned={handleBulkAssigned}
          techFilter={techFilter}
          setTechFilter={setTechFilter}
          onTechColorChange={handleTechColorChange}
        />
      ) : viewMode === "calendar" ? (
        <JobCalendarView
          currentMonth={currentMonth}
          setCurrentMonth={setCurrentMonth}
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          getJobsForDate={getJobsForDate}
          openNewJobDialog={openNewJobDialog}
          handleEdit={handleEdit}
          confirmDelete={confirmDelete}
          handleSendReminder={handleSendReminder}
          handleSendToTech={handleSendToTech}
          sendingToTech={sendingToTech}
          onPreview={setPreviewJob}
          onRenameJob={handleRenameJob}
        />
      ) : viewMode === "timeline" ? (
        <JobTimelineView
          jobs={filteredJobs.filter((j) => !["completed", "cancelled"].includes(j.status))}
          teamMembers={teamMembers}
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          weekStart={weekStart}
          setWeekStart={setWeekStart}
          onPreview={setPreviewJob}
          handleEdit={handleEdit}
          onJobsChanged={fetchData}
          hoursPref={timelineHoursPref}
          saveHoursPref={saveTimelineHoursPref}
          onCreateJob={(defaults) => navigate("/dashboard/jobs/new", { state: { schedulePrefill: defaults, returnTo: "/dashboard/jobs" } })}
        />
      ) : (
        <JobListView
          jobs={filteredJobs}
          openNewJobDialog={openNewJobDialog}
          handleEdit={handleEdit}
          confirmDelete={confirmDelete}
          handleSendReminder={handleSendReminder}
          handleSendToTech={handleSendToTech}
          sendingToTech={sendingToTech}
          onPreview={setPreviewJob}
          onEndReached={isPaginatedMode ? handleLoadMore : undefined}
          loadingMore={loadingMore}
          hasMore={hasMore}
          onRenameJob={handleRenameJob}
          sourceMap={jobSourceMap}
          onSourceClick={(sid) => setSourceFilter(sid)}
        />
      )}

      {/* Job Preview Modal — map view uses the map-focused variant with the
          quick technician assignment bar; other views use the standard modal */}
      {previewJob && (viewMode === "map" ? (
        <MapJobDetailsModal
          job={previewJob}
          onClose={() => setPreviewJob(null)}
          onEdit={handleEdit}
          onDelete={confirmDelete}
          onSendReminder={handleSendReminder}
          onSendToTech={handleSendToTech}
          onSendReviewRequest={handleSendReviewRequest}
          onNewJobForCustomer={handleNewJobForCustomer}
          onOpenCustomer={openCustomerFromJob}
          sendingToTech={sendingToTech}
          sendingReview={sendingReview}
          paymentTypes={paymentTypes}
          onRename={handleRenameJob}
          teamMembers={teamMembers}
          onAssigned={applyAssignmentLocal}
        />
      ) : (
        <JobPreviewModal
          job={previewJob}
          onClose={() => setPreviewJob(null)}
          onEdit={handleEdit}
          onDelete={confirmDelete}
          onSendReminder={handleSendReminder}
          onSendToTech={handleSendToTech}
          onSendReviewRequest={handleSendReviewRequest}
          onNewJobForCustomer={handleNewJobForCustomer}
          onOpenCustomer={openCustomerFromJob}
          sendingToTech={sendingToTech}
          sendingReview={sendingReview}
          paymentTypes={paymentTypes}
          onRename={handleRenameJob}
        />
      ))}

      {/* Customer profile opened from the job preview */}
      <CustomerModal
        open={jobCustomerOpen}
        onOpenChange={setJobCustomerOpen}
        customer={jobCustomer}
        onSaved={() => fetchData()}
        onDeleted={() => { setJobCustomerOpen(false); fetchData(); }}
        onOpenJob={openJobFromCustomer}
      />
    </div>
  );
};

export default JobsPage;
